import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createApiCall } from 'services'

type UseAcknowledgeInvalidScansOptions = {
  onSuccess?: (data?: unknown) => void
  onError?: (error?: unknown) => void
}

/**
 * VRG-491 — mark a certificate's invalid scans as reviewed.
 *
 * The server records ONE instant (`certificates.invalid_scans_acknowledged_at`) and the
 * invalid-scan count is computed over scans strictly newer than it. So this clears the
 * indicator, and the next invalid scan raises it again by itself. Nothing is deleted: every
 * invalid scan stays in the Scans tab.
 *
 * ## The invalidation set is the point
 *
 * The count is read from TWO places that must not disagree, and both are invalidated here:
 *   - `['certificates', 'detail', id]` — the certificate DETAIL payload, which carries
 *     the header's tally (`invalid_scan_count` / `invalid_reason_counts`) and the
 *     `has_invalid_scans` banner flag, and
 *   - `['certificates']` — the certificates LIST, whose row cell renders the same
 *     indicator from the same server-computed field.
 * Invalidating only one would leave the other showing the old count until it happened to
 * refetch, so the same certificate would read differently on two screens.
 *
 * ⚠️ A third key, `['certificate-invalid-scans', id]`, used to be listed here and was
 * DEAD: the hook it named (`useCertificateInvalidScans`, VRG-491) actually keyed its
 * query `['certificate-admin-row', id]`, so this line matched nothing and the header's
 * tally was never invalidated by an acknowledgement at all. It cleared only because that
 * hook's own component re-rendered. VRG-528 removed the separate request entirely — the
 * count now arrives on the detail payload — so the key that matters is the detail one
 * above, and it is real.
 */
export const useAcknowledgeInvalidScans = (
  options?: UseAcknowledgeInvalidScansOptions
) => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/admin/${id}/acknowledge-invalid-scans`,
      })
      return response.data
    },
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ['certificates', 'detail', id] })
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  return {
    acknowledgeInvalidScans: (id: string) => mutation.mutate(id),
    isAcknowledging: mutation.isPending,
  }
}
